import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { TablePagination, Checkbox, Select, MenuItem, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import RouteRequiredPermitOverridesValidator from "../validators/RouteRequiredPermitOverridesValidator";
import AirportLookup from "../../Airports/components/AirportsLookup";
import CountryLookup from "../../Settings/components/CountryLookup";

import { useRouteRequiredPermitOverrides, useAddRouteRequiredPermitOverride, useUpdateRouteRequiredPermitOverride, useDeleteRouteRequiredPermitOverride } from "../../../services/permits";
import { defaultComponents } from '../../../controls/Material-Table-Components';

const RouteRequiredPermitOverrideList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(RouteRequiredPermitOverridesValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddRouteRequiredPermitOverride = useAddRouteRequiredPermitOverride();
  const handleUpdateRouteRequiredPermitOverride = useUpdateRouteRequiredPermitOverride();
  const handleDeleteRouteRequiredPermitOverride = useDeleteRouteRequiredPermitOverride();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const routeRequiredPermitOverrides = useRouteRequiredPermitOverrides({tableRef, queryClient, extraKeys});

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    // const formatChars={ 
    //   "U": "[A-Z]", 
    //   "A": "[A-Z0-9]", 
    //   "a": "[A-Za-z0-9]",
    //   "i": "[A-Za-z0-9\\-]",
    //   "9": "[0-9]",
    //   "D": "[0-9.]",
    //   "-": "-",
    //   "x": "."
    // }

    
    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Permit Type",
        field: "permitType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "OVERFLIGHT",
        editComponent: (props) => {
          const comboValues = [{"code":"OVERFLIGHT","value":"Overflight"}];//,{"code":"LANDING","value":"Landing"},{"code":"DEPARTURE","value":"Departure"}
          return (
            <Select
              value={props.value == null?'':props.value}
              onChange={
                (evt, data) => {
                  props.onChange(data.props.value);
                }
              }
              renderValue={(value) => `${value}`}
            >
              {(comboValues) ? 
                comboValues.map((record) => {
                    return (
                      <MenuItem key={record.code} value={record.value}>{record.code} - {record.value}</MenuItem>
                    );
                }) :
                null
              }
            </Select>
          )
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Origin",
        field: "originAirportCode",
        initialEditValue: "",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        title: "",
        field: "originAirportName",
        initialEditValue: "",
        editable: "never"
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Destination",
        field: "destinationAirportCode",
        initialEditValue: "",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        title: "",
        field: "destinationAirportName",
        initialEditValue: "",
        editable: "never"
      },
      {
        ...baseColumn,
        title: "Country",
        field: "countryCode",
        initialEditValue: "",
        editComponent: (props) => (
          <CountryLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        title: "",
        field: "countryName",
        initialEditValue: "",
        editable: "never"
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Required",
        field: "required",
        type: "boolean",
        emptyValue: false,
        initialEditValue: true, 
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.required === true} value="true" />        
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Route Required Permit Override Management"
            columns={getColumns({canEdit: state.canEdit})}
            data={routeRequiredPermitOverrides.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddRouteRequiredPermitOverride.mutate({record: newData, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateRouteRequiredPermitOverride.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteRouteRequiredPermitOverride.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default RouteRequiredPermitOverrideList;
